import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ConfiguredForm } from './components/formConfiguration';
import { getTariffName } from './components/ChatTariffs';

// Without this dayjs ignores the format and native-Date-parses DD.MM as MM.DD.
dayjs.extend(customParseFormat);

type FormValues = ConfiguredForm['values'];
type FormTariff = FormValues['chat'][number];

const formatTariff = (tariff: FormTariff, name: () => string) =>
  tariff.is_active
    ? {
      is_active: true,
      name: name(),
      duration: Number(tariff.duration),
      price: Number(tariff.price),
    }
    : {
      is_active: false,
      name: null,
      duration: null,
      price: null,
    };

// Position in the payload -> position in the form, so that a server error on
// tariffs.chats.N lands on the card the user actually filled in.
export type ChatOrder = number[];

export const formatToBackendCompatibleJSON = (data: FormValues, t: any) => {
  // The API derives is_default from the first active entry of each group.
  const chats = data.chat
    .map((tariff, formIndex) => ({ tariff, formIndex }))
    .sort((a, b) => Number(b.tariff.is_active) - Number(a.tariff.is_active));

  return {
    payload: {
      full_name: data.full_name,
      phone: data.phone.replaceAll(/[()\s\-_]/g, ''),
      dob: dayjs(data.dob, 'DD.MM.YYYY').format('YYYY-MM-DD'),
      specialisation: data.specialisation,
      experience: Number(data.experience),
      education: data.education,
      about: data.about,
      tariffs: {
        chats: chats.map(({ tariff }) =>
          formatTariff(tariff, () => getTariffName(tariff.duration, t))),
        videos: data.video.map(tariff =>
          formatTariff(tariff, () => t('success.result.videoconsultation'))),
      },
    },
    chatOrder: chats.map(({ formIndex }) => formIndex) as ChatOrder,
  };
};

const stepOfPersonalField: Record<string, number> = {
  full_name: 0,
  phone: 0,
  dob: 0,
  specialisation: 1,
  experience: 1,
  education: 1,
  about: 1,
};

export const SUBMIT_STEP = 3;
export const RESULT_STEP = 4;

const stepOfField = (field: string) => {
  if (field in stepOfPersonalField) return stepOfPersonalField[field];
  if (field.startsWith('video')) return 2;
  return SUBMIT_STEP;
};

export const firstErrorStep = (fields: string[]) =>
  fields.map(stepOfField).reduce((first, step) => Math.min(first, step), SUBMIT_STEP);

export const toFormField = (field: string, chatOrder: ChatOrder) => {
  if (field === 'tariffs.chats') return 'chat';
  const chat = field.match(/^tariffs\.chats\.(\d+)\.(\w+)$/);
  if (chat) return `chat.${chatOrder[Number(chat[1])] ?? chat[1]}.${chat[2]}`;
  return field.replace(/^tariffs\.videos\./, 'video.');
};

// A message on a field no component renders is worse than no mapping at all:
// the wizard jumps to a step showing nothing. Everything outside this set is
// shown as plain text instead.
export const renderableFields = (values: FormValues) => {
  const fields = new Set(Object.keys(stepOfPersonalField));
  fields.add('chat');
  const addTariff = (group: 'chat' | 'video') =>
    values[group].forEach((tariff, index) => {
      if (!tariff.is_active) return;
      fields.add(`${group}.${index}.duration`);
      fields.add(`${group}.${index}.price`);
    });
  addTariff('chat');
  addTariff('video');
  return fields;
};
